/**
 * Canonical file type identifiers stored as values in the s3KeySingleFile map.
 * Must stay in sync with the backend constants in models/file_types.go.
 */
export const FILE_TYPES = {
	XCOM: 'xcom',
	DTC: 'dtc',
	JSONDIFF: 'jsondiff',
	TXT: 'txt',
	XML: 'xml',
	E2: 'e2',
	ATS: 'ats',
	AGENT: 'agent',
	GADGET: 'gadget',
	SOPS: 'sops',
	HTML: 'html',
	JSON: 'json',
	CSV: 'csv',
	PDF: 'pdf',
	JPG: 'jpg',
	JPEG: 'jpeg',
	PNG: 'png',
	MP4: 'mp4',
	LOG: 'log',
	ZIP: 'zip',
	OTHER: 'other',
} as const;

export type FileType = (typeof FILE_TYPES)[keyof typeof FILE_TYPES];

/**
 * Options for the file type dropdown in the upload UI.
 * Each entry has a label shown to the user and the value sent to the API.
 */
export interface FileTypeOption {
	label: string;
	value: FileType;
	/** When true, this file type is only available at the main suite upload level. */
	mainSuiteOnly?: boolean;
}

/**
 * Maps file extensions to Analyzer file types.
 */
export const EXTENSION_TO_FILE_TYPE: Record<string, FileType> = {
	'.txt': FILE_TYPES.TXT,
	'.xml': FILE_TYPES.XML,
	'.json': FILE_TYPES.JSON,
	'.csv': FILE_TYPES.CSV,
	'.pdf': FILE_TYPES.PDF,
	'.jpg': FILE_TYPES.JPG,
	'.jpeg': FILE_TYPES.JPEG,
	'.png': FILE_TYPES.PNG,
	'.mp4': FILE_TYPES.MP4,
	'.log': FILE_TYPES.LOG,
	'.zip': FILE_TYPES.ZIP,
	'.html': FILE_TYPES.HTML,
	'.htm': FILE_TYPES.HTML,
	'.e2': FILE_TYPES.E2,
	'.jsondiff': FILE_TYPES.JSONDIFF,
};

/**
 * Maps file extensions to the Content-Type to store on S3 at upload time.
 * The browser's File.type is unreliable for many extensions (e.g. .log comes
 * through empty), which previously caused files to be stored as
 * application/octet-stream and download instead of opening in-browser.
 * Deriving the type from the extension lets text/images/PDF render in a new tab.
 */
export const EXTENSION_TO_CONTENT_TYPE: Record<string, string> = {
	'.txt': 'text/plain; charset=utf-8',
	'.log': 'text/plain; charset=utf-8',
	'.json': 'application/json; charset=utf-8',
	'.jsondiff': 'application/json; charset=utf-8',
	'.xml': 'application/xml; charset=utf-8',
	'.csv': 'text/csv; charset=utf-8',
	'.xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
	'.html': 'text/html; charset=utf-8',
	'.htm': 'text/html; charset=utf-8',
	'.pdf': 'application/pdf',
	'.jpg': 'image/jpeg',
	'.jpeg': 'image/jpeg',
	'.png': 'image/png',
	'.mp4': 'video/mp4',
	'.zip': 'application/zip',
};

/**
 * Resolves the Content-Type to store for a file, preferring an extension-based
 * type (so files reliably open in-browser when viewed) and falling back to the
 * browser-reported File.type, then application/octet-stream.
 * @param fileName
 * @param browserType
 */
export function resolveContentType(fileName: string, browserType?: string): string {
	const dot = fileName.lastIndexOf('.');
	const ext = dot >= 0 ? fileName.slice(dot).toLowerCase() : '';
	return EXTENSION_TO_CONTENT_TYPE[ext] || browserType || 'application/octet-stream';
}

/**
 * Maps executor FileType values to Analyzer file types (Results environment).
 */
export const EXECUTOR_FILE_TYPE_TO_ANALYZER: Record<string, FileType> = {
	OffboardAgent: FILE_TYPES.AGENT,
	Ats: FILE_TYPES.ATS,
	E2: FILE_TYPES.E2,
	OffboardGadget: FILE_TYPES.GADGET,
	Xcom: FILE_TYPES.XCOM,
	Sops: FILE_TYPES.SOPS,
};

/**
 * File types that should display a badge in the file list dialog.
 */
export const SPECIAL_FILE_TYPES = new Set<FileType>([FILE_TYPES.ATS, FILE_TYPES.AGENT, FILE_TYPES.GADGET, FILE_TYPES.E2, FILE_TYPES.XCOM, FILE_TYPES.SOPS, FILE_TYPES.DTC, FILE_TYPES.JSONDIFF]);

export const FILE_TYPE_OPTIONS: FileTypeOption[] = [
	{ label: 'XCOM Readout', value: FILE_TYPES.XCOM, mainSuiteOnly: true },
	{ label: 'DTC Results', value: FILE_TYPES.DTC },
	{ label: 'JSON Diff', value: FILE_TYPES.JSONDIFF },
	{ label: 'Text', value: FILE_TYPES.TXT },
	{ label: 'XML', value: FILE_TYPES.XML },
	{ label: 'E2', value: FILE_TYPES.E2, mainSuiteOnly: true },
	{ label: 'ATS', value: FILE_TYPES.ATS, mainSuiteOnly: true },
	{ label: 'Agent', value: FILE_TYPES.AGENT, mainSuiteOnly: true },
	{ label: 'Gadget', value: FILE_TYPES.GADGET, mainSuiteOnly: true },
	{ label: 'SOPS', value: FILE_TYPES.SOPS, mainSuiteOnly: true },
	{ label: 'HTML', value: FILE_TYPES.HTML },
	{ label: 'JSON', value: FILE_TYPES.JSON },
	{ label: 'CSV', value: FILE_TYPES.CSV },
	{ label: 'PDF', value: FILE_TYPES.PDF },
	{ label: 'JPG', value: FILE_TYPES.JPG },
	{ label: 'JPEG', value: FILE_TYPES.JPEG },
	{ label: 'PNG', value: FILE_TYPES.PNG },
	{ label: 'MP4', value: FILE_TYPES.MP4 },
	{ label: 'Log', value: FILE_TYPES.LOG },
	{ label: 'ZIP', value: FILE_TYPES.ZIP },
	{ label: 'Other', value: FILE_TYPES.OTHER },
];
